import { isSamlAuthInProgress } from "../capacitor/auth-callback";
import { isNativePlatform } from "../capacitor/platform";
import { isForceUpdateActive } from "../capacitor/force-update-state";

/**
 * A repeating timer backed by a Web Worker so it keeps firing in background
 * tabs (browsers throttle main-thread timers to once per minute after a few
 * minutes hidden). Falls back to setInterval when Workers are unavailable.
 *
 * Shared by the status-polling and IP-discovery loops, which run independently.
 */
export function createPollTimer(onTick: () => void): { start: (interval: number) => void; stop: () => void } {
  let worker: Worker | null = null;
  let fallbackTimerId: ReturnType<typeof setInterval> | null = null;

  try {
    worker = new Worker(new URL("./poll-worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = () => onTick();
  } catch {
    // Web Workers unavailable, will use fallback
    worker = null;
  }

  return {
    start(interval: number) {
      if (worker) {
        worker.postMessage({ command: "start", interval });
      } else {
        if (fallbackTimerId) clearInterval(fallbackTimerId);
        fallbackTimerId = setInterval(onTick, interval);
      }
    },
    stop() {
      if (worker) {
        worker.postMessage({ command: "stop" });
        worker.terminate();
        worker = null;
      }
      if (fallbackTimerId) {
        clearInterval(fallbackTimerId);
        fallbackTimerId = null;
      }
    },
  };
}

/**
 * Whether background network polling (status fetch or IP discovery) should be
 * suppressed regardless of cadence.
 */
export function shouldSuppressBackgroundPolling(): boolean {
  // Suppress while a SAML auth flow is in progress on native: the app resumes
  // from the Chrome Custom Tab before the token exchange completes, and a
  // premature request would get a 401 that wipes the auth store.
  if (isNativePlatform() && isSamlAuthInProgress()) {
    return true;
  }
  // No point polling once the force-update gate has tripped; the UI is pinned
  // to the ForceUpdate screen and the grant state is irrelevant.
  if (isForceUpdateActive()) {
    return true;
  }
  return false;
}
